import React, { useContext } from 'react';
import classes from './Timeslot.module.scss';
import {
  CategoryColor,
  GenericProfileType,
  Network,
  SocialOAuthProvider,
  TimeFormatSetting,
  TimeslotDay
} from '../../../../models/graphql';
import { GetTimeslots_workspace_timeslots_socialProfiles } from '../../../../models/legacy-graphql';
import { CategoryBorderCard } from '../../../../components/containers/CategoryBorderCard';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { SquareButtonIconsEnum } from '../../../../constants/square-button-icons';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import classNames from 'classnames';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { Void } from '../../../../models/basic-objects';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { ContainerListItemExtended } from '../../../../components/actions/ButtonMenu/ButtonMenu.view';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';
import { Debugger } from '../../../../components/containers/Debugger';

type TimeslotActionKeys = 'edit' | 'delete' | 'clone';
export type TimeslotActionsType = {
  [key in TimeslotActionKeys]: VoidFunction;
};

export type TimeslotSocialProfileType = {
  connected: boolean;
  disabled: boolean;
  provider: SocialOAuthProvider | null;
  network: Network;
  genericProfile?: GenericProfileType;
  image: string;
  id: string;
  name: string;
  __typename: GetTimeslots_workspace_timeslots_socialProfiles['__typename'];
};

export type TimeslotViewProps = {
  actions?: TimeslotActionsType;
  categoryId: string;
  colour: CategoryColor | null;
  day: TimeslotDay;
  hovering: boolean; // while we drag other timeslot from the same category
  hoverRelatedHandler: Void<boolean>;
  id: string;
  isDragging: boolean;
  isListView: boolean;
  name: string;
  socialProfiles: TimeslotSocialProfileType[];
  time: string;
  timeFormat: TimeFormatSetting;
  onboardingContent: boolean;
};

export const TimeslotView = (props: TimeslotViewProps): JSX.Element => {
  const { showOnboarding, state } = useContext(OnboardingContext);
  const { version } = state;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.timeslots;
  const listItems: Array<ContainerListItem | ContainerListItemExtended> = props.actions
    ? [
        { action: props.actions.edit, label: 'Edit' },
        ...(props.isListView ? [] : [{ action: props.actions.clone, label: 'Duplicate' }]),
        {
          action: props.actions.delete,
          label: 'Delete',
          ...(props.onboardingContent && disableDelete && showOnboarding
            ? { disabled: true, disabledTooltipText: 'You’ll be able to delete these timeslots later.' }
            : {})
        }
      ]
    : [];

  const globalClasses = classNames(classes.Timeslot, classes[`Timeslot${props.colour}`], {
    [classes.TimeslotDragging]: props.isDragging,
    [classes[`TimeslotHoverable${props.colour}`]]: !props.isListView,
    [classes[`TimeslotHovering${props.colour}`]]: props.hovering && !props.isDragging
  });

  const profileWrapperClasses = classNames('cell', 'shrink', classes.SocialProfileWrapper);
  const formattedTime = PostingPlanHelpers.getTime(props.day, props.time, props.timeFormat);
  const socialProfiles = props.socialProfiles.map((profile, index) => (
    <div className={profileWrapperClasses} key={index}>
      <SocialProfile
        size={'small'}
        socialProfile={{
          id: profile.id,
          provider: profile.provider,
          network: profile.network,
          genericProfile: profile.genericProfile,
          name: profile.name
        }}
        disconnected={!profile.connected}
        disabled={profile.disabled}
        image={profile.image}
      />
    </div>
  ));
  return (
    <CategoryBorderCard borderColor={props.colour || CategoryColor.LIGHT_GREY} borderPosition={'Left'} applyCardBorder>
      <Debugger tooltipContent={'Timeslot ID: ' + props.id} modalContent={props} />
      <div
        className={globalClasses}
        data-event-id={props.id}
        {...(props.isListView
          ? {}
          : {
              onMouseEnter: () => props.hoverRelatedHandler(true),
              onMouseLeave: () => props.hoverRelatedHandler(false)
            })}>
        {listItems.length > 0 && (
          <div className={classes.SquareButtonWrapper}>
            <ButtonMenu
              elementType={'button'}
              buttonType={'transparent'}
              listItems={listItems}
              isSquareButton={true}
              side={'right'}
              icon={SquareButtonIconsEnum.dotsShifted}
            />
          </div>
        )}
        <div className={classes.TitleWrapper}>
          <h6 className={classes.Title}>{props.name.toLocaleUpperCase()}</h6>
          <time className={classes.Time} data-date={formattedTime.date.toISOString()}>
            {formattedTime.time}
          </time>
        </div>
        <div className={'grid-x'}>{socialProfiles}</div>
      </div>
    </CategoryBorderCard>
  );
};
